import {WAR_PHASE_STAGES} from "../../constants/phases";
import {
  selectCurrentPlayer,
  selectUnits,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {selectInspectedHexKey, selectSelectedUnitId} from "../../redux/reducers/privateStateReducer/selectors";
import {autoSelectUnit, clearInspectedHex, deselectUnit, skipUnitTurn} from "../../redux/actions";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {selectPhaseProgress} from "../gameState/progress/selectPhaseProgress";
import {checkIfUnitTurnSkipShown, getUnitTurnSkipKey} from "../gameState/progress/unitTurnSkips";
import {checkIfUnitAutoSelectionActive, checkIfUnitTurnSkipStage} from "./checkIfUnitAutoSelectionActive";
import {pickNextActionableUnit} from "./pickNextActionableUnit";

// Команды автовыбора отрядов (change unit-auto-selection). Их зовут пробел и
// кнопка пропуска в футере, а UnitAutoSelector — после действия отряда

// Этапы, где после действия выбор остается на отряде: в футере его результат
// и переброс костей. К следующему переходит команда «дальше»
export const RESULT_HOLDING_STAGES = Object.freeze([WAR_PHASE_STAGES.SHOOTING, WAR_PHASE_STAGES.ABILITIES]);

// Дела этапа игрока, который сейчас ходит: на этапах войны это текущий игрок
const getActingProgress = (state) => {
  const playerId = selectCurrentPlayer(state)?.id;
  return playerId ? {playerId, progress: selectPhaseProgress(state, playerId)} : {playerId: null, progress: null};
};

const getSelectedOwnUnit = (state, playerId) => {
  const selectedUnitId = selectSelectedUnitId(state);
  const unit = selectedUnitId && (selectUnits(state) || []).find((item) => item.id === selectedUnitId);
  return unit && unit.ownerId === playerId ? unit : null;
};

/**
 * Можно ли пропустить ход выбранного отряда: свой ход на этапе с делами
 * отрядов, выбран свой отряд, который может действовать и еще не пропускал ход
 * (пропущенный уже не в actionableIds)
 */
export const checkIfSelectedUnitTurnSkippable = (state) => {
  if (!checkIfUnitTurnSkipStage(state)) {
    return false;
  }
  const {playerId, progress} = getActingProgress(state);
  const unit = getSelectedOwnUnit(state, playerId);
  return Boolean(unit && progress?.actionableIds.includes(unit.id));
};

/**
 * Выбран ли свой отряд, который уже спит: пропустил ход на этом этапе и с тех
 * пор не действовал (игрок выбрал его сам, чтобы посмотреть или передумать)
 */
export const checkIfSelectedUnitSleeping = (state) => {
  if (!checkIfUnitTurnSkipStage(state)) {
    return false;
  }
  const unit = getSelectedOwnUnit(state, getActingProgress(state).playerId);
  return Boolean(unit && checkIfUnitTurnSkipShown(state, unit));
};

/**
 * Можно ли перейти «дальше»: на стрельбе или способностях выбран свой отряд,
 * который уже действовал на этом этапе (выбор остался на нем ради результата)
 */
export const checkIfSelectedUnitCanAdvance = (state) => {
  if (!checkIfUnitTurnSkipStage(state) || !RESULT_HOLDING_STAGES.includes(selectWarPhaseStage(state))) {
    return false;
  }
  const {playerId, progress} = getActingProgress(state);
  const unit = getSelectedOwnUnit(state, playerId);
  // Спящий (пропустил ход и не действовал) — не «дальше», а свой режим кнопки;
  // пропустивший и потом выстреливший — действовал, ему «дальше»
  return Boolean(unit && !progress?.actionableIds.includes(unit.id)
    && !checkIfUnitTurnSkipShown(state, unit));
};

/**
 * Выбрать автоматически следующий отряд — ближайший к originHexKey из тех,
 * кто может действовать и не пропустил ход — и подвести к нему кадр. Звук
 * выбора тот же, что при клике по отряду.
 * @returns {?Object} выбранный отряд или null, если выбирать некого
 */
export const autoSelectNextUnit = (store, originHexKey) => {
  const state = store.getState();
  if (!checkIfUnitAutoSelectionActive(state)) {
    return null;
  }
  const {progress} = getActingProgress(state);
  const unit = pickNextActionableUnit({
    actionableIds: progress?.actionableIds || [],
    units: selectUnits(state),
    originHexKey,
  });
  if (!unit) {
    return null;
  }

  store.dispatch(autoSelectUnit(unit.id));
  REGISTRY_SINGLETON.getSoundManager()?.playUnitSelect?.(unit.type, unit.ownerId, selectCurrentPlayer(state), unit);
  REGISTRY_SINGLETON.getGameScene()?.focusCameraOnAutoSelectedUnit?.(unit.hexKey);
  return unit;
};

/**
 * Подвести кадр к уже выбранному отряду — в начале этапа, когда автовыбор
 * сохраняет выбор игрока (отряд остался выбранным с прошлого этапа)
 */
export const focusCameraOnSelectedUnit = (store) => {
  const state = store.getState();
  if (!checkIfUnitAutoSelectionActive(state)) {
    return;
  }
  const unit = getSelectedOwnUnit(state, getActingProgress(state).playerId);
  if (unit) {
    REGISTRY_SINGLETON.getGameScene()?.focusCameraOnAutoSelectedUnit?.(unit.hexKey);
  }
};

/**
 * Пропустить ход выбранного отряда: он обработан, на поле — отметка «zzZ», выбор
 * переходит к ближайшему (при включенном автовыборе) или снимается
 * @returns {boolean} пропущен ли ход
 */
export const skipSelectedUnitTurn = (store) => {
  const state = store.getState();
  if (!checkIfSelectedUnitTurnSkippable(state)) {
    return false;
  }
  const {playerId} = getActingProgress(state);
  const unit = getSelectedOwnUnit(state, playerId);

  store.dispatch(skipUnitTurn(unit.id, getUnitTurnSkipKey(state, playerId)));
  store.dispatch(deselectUnit());
  autoSelectNextUnit(store, unit.hexKey);
  return true;
};

/**
 * «Дальше» после выстрела или способности: выбрать ближайший к действовавшему
 * отряд. Если выбирать некого, выбор остается на нем
 * @returns {boolean} выбран ли следующий
 */
export const advanceFromSelectedUnit = (store) => {
  const state = store.getState();
  if (!checkIfSelectedUnitCanAdvance(state)) {
    return false;
  }
  const {playerId} = getActingProgress(state);
  const unit = getSelectedOwnUnit(state, playerId);
  return Boolean(autoSelectNextUnit(store, unit.hexKey));
};

/**
 * Можно ли от выбранного спящего отряда перейти к следующему: автовыбор
 * включен и есть отряд, который может действовать и не спит. Иначе кнопка сна
 * у такого отряда заблокирована
 */
export const checkIfSleepingUnitCanAdvance = (state) => checkIfSelectedUnitSleeping(state)
  && checkIfUnitAutoSelectionActive(state)
  && getActingProgress(state).progress?.actionableIds.length > 0;

/**
 * Кнопка сна у отряда, который уже спит (игрок выбрал его сам): сон
 * остается, выбор переходит к ближайшему отряду, который может действовать
 * @returns {boolean} выбран ли следующий
 */
export const advanceFromSleepingUnit = (store) => {
  const state = store.getState();
  if (!checkIfSleepingUnitCanAdvance(state)) {
    return false;
  }
  const unit = getSelectedOwnUnit(state, getActingProgress(state).playerId);
  return Boolean(autoSelectNextUnit(store, unit.hexKey));
};

/**
 * Пробел, когда пропускать и переходить «дальше» нечего: выделение снято
 * (клик по пустой ячейке, осмотр ячейки) или выбранному отряду делать нечего.
 * Выбирает ближайший к нему (или к центру свободной области) отряд, который
 * может действовать, и закрывает осмотр ячейки — как «следующий отряд» в
 * Civilization. Только при включенном автовыборе
 * @returns {boolean} выбран ли отряд
 */
export const selectNearestUnitBySpace = (store) => {
  const state = store.getState();
  if (!checkIfUnitAutoSelectionActive(state)) {
    return false;
  }
  const selectedUnit = getSelectedOwnUnit(state, getActingProgress(state).playerId);
  const originHexKey = selectedUnit?.hexKey
    || REGISTRY_SINGLETON.getGameScene()?.getFreeAreaCenterHexKey?.()
    || null;
  const unit = autoSelectNextUnit(store, originHexKey);
  if (unit && selectInspectedHexKey(store.getState())) {
    store.dispatch(clearInspectedHex());
  }
  return Boolean(unit);
};

// Что делает кнопка в шапке футера (и пробел) прямо сейчас
export const UNIT_TURN_BUTTON_MODES = Object.freeze({
  SKIP: 'skip', // пропустить ход выбранного отряда
  SLEEPING: 'sleeping', // выбранный отряд уже спит — к следующему, сон остается
  ADVANCE: 'advance', // после выстрела или способности — к следующему отряду
});

/**
 * Режим кнопки пропуска в шапке футера или null — тогда кнопки нет (spec
 * unit-auto-selection, «Кнопка пропуска в шапке футера»). У спящего отряда
 * кнопка сна есть всегда, но заблокирована, когда переходить не к кому (см.
 * selectIsUnitTurnButtonDisabled). «Дальше» показывается, только когда
 * автовыбору есть кого выбрать
 */
export const selectUnitTurnButtonMode = (state) => {
  if (checkIfSelectedUnitTurnSkippable(state)) {
    return UNIT_TURN_BUTTON_MODES.SKIP;
  }
  if (checkIfSelectedUnitSleeping(state)) {
    return UNIT_TURN_BUTTON_MODES.SLEEPING;
  }
  if (checkIfSelectedUnitCanAdvance(state) && checkIfUnitAutoSelectionActive(state)
    && getActingProgress(state).progress?.actionableIds.length > 0) {
    return UNIT_TURN_BUTTON_MODES.ADVANCE;
  }
  return null;
};

/**
 * Заблокирована ли кнопка в шапке футера: у спящего отряда — когда все
 * остальные отряды тоже спят или уже сходили (или автовыбор выключен) и
 * переходить не к кому
 */
export const selectIsUnitTurnButtonDisabled = (state) => (
  selectUnitTurnButtonMode(state) === UNIT_TURN_BUTTON_MODES.SLEEPING && !checkIfSleepingUnitCanAdvance(state)
);
